import { StateAliases } from '@helo/cms'
import { tap } from '@helo/utils'
import { flow } from '@helo/utils'

import {
  getUrlStorage,
  setUrlStorage,
} from '../../../utils/url-storage/urlStorage'
import {
  LOCALSTORAGE_PERSISTENCE_PREFIX,
  PersistenceSlice,
  PersistenceState,
} from './persistenceSlice'
import { PersistedItem } from './persistenceSlice.types'

export const persistenceItemsAreTheSame = (
  a: PersistedItem,
  b: PersistedItem,
) => a.key === b.key

export const storeHasItem = (
  inputItem: PersistedItem,
  state: PersistenceState,
) => {
  return state.items.some(item => persistenceItemsAreTheSame(item, inputItem))
}

export const getPersistenceItem = (key: string) => (state: PersistenceState) =>
  state.items.find(i => i.key === key)

const normalizePersistedItem = (item: PersistedItem): PersistedItem => ({
  ...item,
  value: StateAliases.normalizeUrlStorageValue(item.value),
})

export const addItemToStore =
  (item: PersistedItem) =>
  (state: PersistenceState): PersistenceState => {
    if (storeHasItem(item, state)) {
      return {
        ...state,
        items: state.items.map(i =>
          persistenceItemsAreTheSame(item, i) ? item : i,
        ),
      }
    } else {
      return {
        ...state,
        items: state.items.concat(item),
      }
    }
  }

export const persistLocalstorageItem = (item: PersistedItem) =>
  tap(<T extends PersistenceSlice>(store: T) => {
    if (item.where === 'localstorage') {
      const existingItems = retrieveFromLocalStorage(store.persistence)
      const hasExistingItem = existingItems.find(i => i.key === item.key)
      const newItems = hasExistingItem
        ? existingItems.map(current => {
            if (current.key === item.key) return normalizePersistedItem(item)
            return current
          })
        : existingItems.concat(normalizePersistedItem(item))

      localStorage.setItem(
        store.persistence.localstoragePersistenceKey,
        JSON.stringify(newItems),
      )
    }
  })

/** Persists entire persistence state to localstorage */
export const persistLocalStorageItems = () =>
  tap(<T extends PersistenceSlice>(store: T) => {
    const { localstoragePersistenceKey, items } = store.persistence

    const localStorageItems = items
      .filter(i => i.where === 'localstorage')
      .map(normalizePersistedItem)
    //TODO: use a reusable util instead of this.
    localStorage.setItem(
      localstoragePersistenceKey,
      JSON.stringify(localStorageItems),
    )
  })

export const retrieveFromLocalStorage = (
  state: PersistenceState,
): PersistedItem[] => {
  const payload = localStorage.getItem(state.localstoragePersistenceKey)
  if (!payload) return []

  try {
    return (JSON.parse(payload) as PersistedItem[]) ?? []
  } catch (e) {
    console.error('Error loading persistence from localstorage', e)
    return []
  }
}

export const peristUrlStorageItems = () =>
  tap(<T extends PersistenceSlice>(store: T) => {
    const urlPersistedItems = store.persistence.items
      .filter(item => item.where === 'url')
      .map(normalizePersistedItem)

    setUrlStorage(urlPersistedItems)
  })

export const retrieveFromUrlStorage = (): PersistedItem[] => {
  const urlStorageItems = getUrlStorage()
  const urlPersistedItems = urlStorageItems.map(
    ({ key, value }): PersistedItem => ({
      key,
      value: StateAliases.normalizeUrlStorageValue(value),
      where: 'url',
      until: 'navigate',
    }),
  )
  return urlPersistedItems
}

export const updatePersistenceSlice =
  (partial: Partial<PersistenceState>) =>
  <T extends PersistenceSlice>(state: T): T => {
    return {
      ...state,
      persistence: {
        ...state.persistence,
        ...partial,
      },
    }
  }

export const initializeLocalstoragePersistenceKey = (email: string) =>
  flow(
    flow.inputExtends<PersistenceSlice>(),
    <T extends PersistenceSlice>(store: T): T => ({
      ...store,
      persistence: {
        ...store.persistence,
        localstoragePersistenceKey: `${LOCALSTORAGE_PERSISTENCE_PREFIX}_${email}`,
        initialized: true,
      },
    }),
  )
